import type {
	AdapterStatus,
	AdapterVolume,
	AudioPlaybackService,
	BlendedAudioPlaybackServiceArgs,
	PlaybackStatus,
} from "../../application/ports";
import { WebAudioAdapter } from "./WebAudioAdapter";
import type { PlaybackStore } from "@/lib/audio/application/stores/playbackStore";
import { createStore, useStore } from "zustand";
import { combine, subscribeWithSelector } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { isMobileDevice } from "@/lib/utils";
import { StreamingAudioContextAdapter } from "./StreamingAudioContextAdapter";
import type { AudioModel } from "@coffitivity/shared";

interface BlendAdapterState {
	playbackStatus: PlaybackStatus;
	volume: number;
}
export interface BlendStoreProps {
	state: Record<string, BlendAdapterState>;
	updateBlendPlaybackStatus: (trackId: string, status: PlaybackStatus) => void;
	updateBlendVolume: (trackId: string, volume: number) => void;
}

export class BlendedAudioAdapter implements AudioPlaybackService {
	// public properties
	public data: AudioModel;
	public preferredMimeType: "mp3" | "ogg";
	public usesAudioContext = true;
	public onPlay: (() => void) | null = null;
	public onPause: (() => void) | null = null;
	public onEnded: (() => void) | null = null;
	public tracks: AudioPlaybackService[] = [];
	public store;
	public useBlendStateHook: () => BlendStoreProps;

	// private properties
	private playbackStore: PlaybackStore;
	private masterVolume: number;
	private previousBlendState: { trackId: string; status: string }[] = [];

	constructor(_args: BlendedAudioPlaybackServiceArgs) {
		this.data = _args.track;
		this.playbackStore = _args.store;
		this.preferredMimeType = _args.preferredMimeType ?? "mp3";
		this.masterVolume = _args.masterVolume ?? 1;

		const initialBlendState: BlendStoreProps["state"] =
			_args.track.blend.reduce(
				(acc, blend) => {
					acc[blend.track.id] = {
						playbackStatus: "UNINITIALIZED",
						volume: blend.volume,
					};
					return acc;
				},
				{} as BlendStoreProps["state"],
			);

		this.store = createStore<BlendStoreProps>()(
			subscribeWithSelector(
				immer(
					combine({ state: initialBlendState }, (set) => ({
						updateBlendPlaybackStatus: (
							trackId: string,
							status: PlaybackStatus,
						) => {
							set((state) => {
								if (state.state[trackId]) {
									state.state[trackId].playbackStatus = status;
								}
							});
						},
						updateBlendVolume: (trackId: string, volume: number) => {
							set((state) => {
								if (state.state[trackId]) {
									state.state[trackId].volume = volume;
								}
							});
						},
					})),
				),
			),
		);

		this.useBlendStateHook = () => {
			const store = useStore(this.store);
			return store;
		};
	}

	private updateAdapterState(
		trackId: string,
		playbackStatus?: PlaybackStatus,
		volume?: number,
	): void {
		if (playbackStatus) {
			this.store.getState().updateBlendPlaybackStatus(trackId, playbackStatus);
		}
		if (volume) {
			this.store.getState().updateBlendVolume(trackId, volume);
		}
	}

	async load(): Promise<void> {
		try {
			const blendSpec = this.data.blend;

			const trackAdapters = await Promise.all(
				blendSpec.map(async (blend) => {
					let adapter: AudioPlaybackService;
					if (isMobileDevice()) {
						adapter = new WebAudioAdapter({
							store: this.playbackStore,
							isSubTrack: true,
							track: blend.track,
							masterVolume: this.masterVolume,
							preferredMimeType: this.preferredMimeType,
							trackVolume: blend.volume,
							updateAdapterState: this.updateAdapterState.bind(this),
						});
					} else {
						adapter = new StreamingAudioContextAdapter({
							store: this.playbackStore,
							isSubTrack: true,
							track: blend.track,
							masterVolume: this.masterVolume,
							preferredMimeType: this.preferredMimeType,
							trackVolume: blend.volume,
							updateAdapterState: this.updateAdapterState.bind(this),
						});
					}

					await adapter.load();

					return adapter;
				}),
			);

			this.tracks = trackAdapters;
		} catch (error) {
			console.error("Error loading blended tracks:", error);
			throw error; // Rethrow the error to be handled by the caller
		}
	}

	async play(_blendTrackId?: string): Promise<void> {
		try {
			// Update the state so user see a quick updates
			if (this.playbackStore.getState().currentTrack?.id !== this.data.id) {
				this.playbackStore.getState().setCurrentTrack(this.data);
			}

			if (_blendTrackId) {
				const track = this.tracks.find((t) => t.data.id === _blendTrackId);

				if (track) {
					await track.play();
				}
				// TODO: throw error if track not found?
			} else {
				if (
					this.previousBlendState.length > 0 &&
					this.previousBlendState.every((t) => t.status === "PAUSED")
				) {
					// if all tracks were paused, we need to play all tracks
					const playPromises = await Promise.allSettled(
						this.tracks.map((track) => track.play()),
					);

					// if all of the tracks failed to play, we throw an error and keeping moving on to ensure the UI is updated
					if (playPromises.every((p) => p.status === "rejected")) {
						throw new Error("All tracks failed to play");
					}
				} else if (this.previousBlendState.length > 0) {
					// if there's a previous blend state, we need to restore it otherwise just play all tracks
					for (const track of this.tracks) {
						const previousTrackState = this.previousBlendState.find(
							(t) => t.trackId === track.data.id,
						);

						if (previousTrackState) {
							if (previousTrackState.status === "PLAYING") {
								track.play();
							}
						}
					}
				} else {
					// if there's no previous blend state, we need to play all tracks
					const playPromises = await Promise.allSettled(
						this.tracks.map((track) => track.play()),
					);

					// if all of the tracks failed to play, we throw an error and keeping moving on to ensure the UI is updated
					if (playPromises.every((p) => p.status === "rejected")) {
						throw new Error("All tracks failed to play");
					}
				}
			}
			this.playbackStore.getState().setCurrentTrackStatus("PLAYING");

			this.onPlay?.();
		} catch (error) {
			console.error("Error playing blended tracks:", error);
			throw error; // Rethrow the error to be handled by the caller
		}
	}

	async pause(trackId?: string): Promise<void> {
		// set the previous blend state before we pause the tracks
		this.previousBlendState = this.tracks.map((track) => ({
			trackId: track.data.id,
			status: track.isPlaying() ? "PLAYING" : "PAUSED",
		}));

		// ! Make sure to pause playback as soon as possible and prior to the actual pause event
		// this will ensure that the playback state is updated in the store and the UI correctly
		if (trackId) {
			const otherTracks = this.tracks.filter((t) => t.data.id !== trackId);
			const areOtherTracksPaused = otherTracks.every(
				(track) => track.isPlaying() === false,
			);
			const track = this.tracks.find((t) => t.data.id === trackId);
			if (track) {
				if (areOtherTracksPaused) {
					this.playbackStore.getState().setCurrentTrackStatus("PAUSED");
				}
				await track.pause();
			}
		} else {
			this.playbackStore.getState().setCurrentTrackStatus("PAUSED");
			await Promise.all(this.tracks.map((track) => track.pause()));
		}

		this.onPause?.();
	}

	async stop(): Promise<void> {
		await Promise.all(this.tracks.map((track) => track.stop()));
		this.playbackStore.getState().setCurrentTrackStatus("STOPPED");
		this.onPause?.(); // same handler for now
	}

	setLoop(loop: boolean): void {
		for (const track of this.tracks) {
			track.setLoop(loop);
		}
	}

	setTrackVolume(volume: number, trackId: string): void {
		const track = this.tracks.find((t) => t.data.id === trackId);
		if (track) {
			track.setTrackVolume(volume);
		}
	}

	isPlaying(): boolean {
		return this.tracks.some((track) => track.isPlaying());
	}

	async cleanup(): Promise<void> {
		await Promise.all(this.tracks.map((track) => track.cleanup()));
		this.tracks = [];
	}

	status(): AdapterStatus {
		const blendStatus = this.tracks.map((track) => track.status());
		return {
			trackId: this.data.id,
			status: this.isPlaying() ? "PLAYING" : "PAUSED",
			error: null,
			trackVolume: this.masterVolume,
			blendStatus,
		};
	}
}
